import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, TabStopType, convertInchesToTwip, ExternalHyperlink } from "docx";
import { saveAs } from "file-saver";
import { ResumeData, ResumeFormat } from "@/types/resume";
import { formatDateFull, sanitizeText } from "./resumeUtils";
import { SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";

// Harvard Resume Format: Small caps headers, traditional serif layout
// Letter page: 8.5" x 11" = 612pt x 792pt

const sanitizeFileName = (name: string) => {
  return name
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[/\\:*?"<>|]+/g, "-")
    .slice(0, 80);
};

const formatLinkDisplay = (url: string): string => {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
};

const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
};

export const generateHarvardPDF = (
  data: ResumeData,
  format: ResumeFormat = "standard",
  fileNameOverride?: string,
  sectionVisibility: SectionVisibility = defaultSectionVisibility,
  sectionOrder: SectionKey[] = DEFAULT_SECTION_ORDER
): void => {
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
  });

  const marginTop = 29; // ~0.4in
  const marginBottom = 29;
  const marginSide = 40; // ~0.55in
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginSide * 2;
  
  const nameSize = format === 'compact' ? 18 : 21;
  const sectionSizeLarge = 14;
  const sectionSizeSmall = 11;
  const bodySize = 10;
  const lineHeight = 13;
  const sectionSpacing = format === 'compact' ? 6 : 10;

  let yPos = marginTop;

  const checkPageBreak = (neededHeight: number = lineHeight) => {
    if (yPos + neededHeight > pageHeight - marginBottom) {
      doc.addPage();
      yPos = marginTop;
    }
  };

  // Helper to add justified text (mimics text-align: justify)
  const addJustifiedText = (text: string, x: number, maxWidth: number) => {
    const words = text.split(' ').filter(w => w.length > 0);
    if (words.length <= 1) {
      doc.text(text, x, yPos);
      return;
    }
    
    // Check if this line fits or needs wrapping
    const fullWidth = doc.getTextWidth(text);
    if (fullWidth <= maxWidth) {
      // Single line - justify by adding space between words
      const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
      const totalSpaceNeeded = maxWidth - totalWordWidth;
      const spacePerGap = totalSpaceNeeded / (words.length - 1);
      
      let currentX = x;
      words.forEach((word, idx) => {
        doc.text(word, currentX, yPos);
        currentX += doc.getTextWidth(word) + spacePerGap;
      });
    } else {
      // Just use normal text for wrapped lines
      doc.text(text, x, yPos);
    }
  };

  // Helper to add wrapped justified bullet text
  const addJustifiedBullet = (bullet: string, bulletX: number, textX: number, maxWidth: number) => {
    doc.text("•", bulletX, yPos);
    const lines = doc.splitTextToSize(bullet, maxWidth);
    lines.forEach((line: string, idx: number) => {
      checkPageBreak();
      if (idx === lines.length - 1) {
        // Last line - left align
        doc.text(line, textX, yPos);
      } else {
        // Other lines - justify
        addJustifiedText(line, textX, maxWidth);
      }
      yPos += lineHeight;
    });
  };

  // Small caps section header: first letter larger, rest smaller
  const addSectionHeader = (title: string) => {
    yPos += sectionSpacing;
    checkPageBreak(lineHeight * 2);

    const firstLetter = title.charAt(0).toUpperCase();
    const rest = title.slice(1).toUpperCase();
    
    doc.setFont("times", "bold");
    doc.setFontSize(sectionSizeLarge);
    const firstWidth = doc.getTextWidth(firstLetter);
    doc.text(firstLetter, marginSide, yPos);
    
    doc.setFontSize(sectionSizeSmall);
    doc.text(rest, marginSide + firstWidth, yPos);

    const underlineY = yPos + 3;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(marginSide, underlineY, pageWidth - marginSide, underlineY);

    yPos = underlineY + lineHeight;
  };

  // Header - Name centered (bold, not all caps)
  doc.setFontSize(nameSize);
  doc.setFont("times", "bold");
  doc.text(data.personalInfo.fullName || "Your Name", pageWidth / 2, yPos, { align: "center" });
  yPos += nameSize * 0.7;

  // Contact line with clickable labels (LinkedIn, GitHub, Portfolio - not full URLs)
  type ContactItem = { text: string; url?: string };
  const contactItems: ContactItem[] = [];
  if (data.personalInfo.location) contactItems.push({ text: data.personalInfo.location });
  if (data.personalInfo.phone) contactItems.push({ text: data.personalInfo.phone });
  if (data.personalInfo.email) contactItems.push({ text: data.personalInfo.email, url: `mailto:${data.personalInfo.email}` });
  if (data.personalInfo.linkedin) contactItems.push({ text: "LinkedIn", url: normalizeUrl(data.personalInfo.linkedin) });
  if (data.personalInfo.github) contactItems.push({ text: "GitHub", url: normalizeUrl(data.personalInfo.github) });
  if (data.personalInfo.portfolio) contactItems.push({ text: "Portfolio", url: normalizeUrl(data.personalInfo.portfolio) });

  doc.setFontSize(bodySize);
  doc.setFont("times", "normal");
  
  // Calculate total width for centering
  const separator = " | ";
  let totalWidth = 0;
  contactItems.forEach((item, idx) => {
    if (idx > 0) totalWidth += doc.getTextWidth(separator);
    totalWidth += doc.getTextWidth(item.text);
  });
  
  let xPos = (pageWidth - totalWidth) / 2;
  contactItems.forEach((item, idx) => {
    if (idx > 0) {
      doc.text(separator, xPos, yPos);
      xPos += doc.getTextWidth(separator);
    }
    
    if (item.url) {
      doc.setTextColor(0, 0, 238); // Blue link color
      doc.textWithLink(item.text, xPos, yPos, { url: item.url });
      const partWidth = doc.getTextWidth(item.text);
      doc.line(xPos, yPos + 1, xPos + partWidth, yPos + 1);
      doc.setTextColor(0, 0, 0);
    } else {
      doc.text(item.text, xPos, yPos);
    }
    xPos += doc.getTextWidth(item.text);
  });
  
  yPos += lineHeight + sectionSpacing;

  // Section renderers
  const renderEducation = () => {
    if (!(sectionVisibility.education && data.education.length > 0)) return;
    addSectionHeader("Education");
    data.education.forEach((edu) => {
      checkPageBreak(lineHeight * 3);
      doc.setFontSize(bodySize);
      doc.setFont("times", "bold");
      doc.text(edu.institution, marginSide, yPos);
      yPos += lineHeight;
      doc.setFont("times", "italic");
      let degreeText = edu.degree;
      if (edu.field) degreeText += ` ${edu.field}`;
      if (edu.gpa) degreeText += `, GPA: ${edu.gpa}`;
      doc.text(degreeText, marginSide, yPos);
      if (edu.location) doc.text(edu.location, pageWidth - marginSide, yPos, { align: "right" });
      yPos += lineHeight + 2;
    });
  };

  const renderExperience = () => {
    if (!(sectionVisibility.experience && data.experience.length > 0)) return;
    addSectionHeader("Experience");
    data.experience.forEach((exp) => {
      checkPageBreak(lineHeight * 3);
      const startFormatted = formatDateFull(exp.startDate);
      const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
      const dateDisplay = startFormatted || endFormatted
        ? `${startFormatted || ""}${startFormatted && endFormatted ? " – " : ""}${endFormatted || ""}`
        : "";
      doc.setFontSize(bodySize);
      doc.setFont("times", "bold");
      doc.text(exp.jobTitle, marginSide, yPos);
      if (dateDisplay) doc.text(dateDisplay, pageWidth - marginSide, yPos, { align: "right" });
      yPos += lineHeight;
      doc.setFont("times", "italic");
      doc.text(exp.company, marginSide, yPos);
      const locationDisplay = [exp.location, exp.workType].filter(Boolean).join(" | ");
      if (locationDisplay) doc.text(locationDisplay, pageWidth - marginSide, yPos, { align: "right" });
      yPos += lineHeight;
      doc.setFont("times", "normal");
      (exp.bullets || []).filter(Boolean).forEach((bullet) => {
        checkPageBreak();
        addJustifiedBullet(bullet, marginSide + 6, marginSide + 14, contentWidth - 18);
      });
      yPos += 2;
    });
  };

  const renderSkills = () => {
    if (!(sectionVisibility.skills && data.coreStrengths && data.coreStrengths.length > 0 && data.coreStrengths.some(c => c.category && c.skills))) return;
    addSectionHeader("Technical Skills");
    data.coreStrengths.filter(c => c.category && c.skills).forEach((cat) => {
      checkPageBreak();
      doc.setFontSize(bodySize);
      doc.setFont("times", "bold");
      const catText = `${sanitizeText(cat.category)}: `;
      doc.text(catText, marginSide, yPos);
      const catWidth = doc.getTextWidth(catText);
      doc.setFont("times", "normal");
      const sanitizedSkills = sanitizeText(cat.skills);
      const skillLines = doc.splitTextToSize(sanitizedSkills, contentWidth - catWidth);
      doc.text(skillLines[0], marginSide + catWidth, yPos);
      yPos += lineHeight;
      for (let i = 1; i < skillLines.length; i++) {
        checkPageBreak();
        doc.text(skillLines[i], marginSide + 10, yPos);
        yPos += lineHeight;
      }
    });
  };

  const renderCustom = () => {
    if (!sectionVisibility.customSections) return;
    // Projects
    const projSections = (data.customSections || []).filter((s) => /project/i.test(s.title || ""));
    projSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            checkPageBreak();
            doc.setFontSize(bodySize);
            doc.setFont("times", "normal");
            addJustifiedBullet(item.title, marginSide + 6, marginSide + 14, contentWidth - 18);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        if (hasContent) {
          checkPageBreak(lineHeight * 2);
          doc.setFontSize(bodySize);
          doc.setFont("times", "bold");
          let currentX = marginSide;
          doc.text(item.title, currentX, yPos);
          currentX += doc.getTextWidth(item.title);
          if (item.subtitle) {
            doc.setFont("times", "italic");
            const subtitleText = ` | ${item.subtitle}`;
            doc.text(subtitleText, currentX, yPos);
            currentX += doc.getTextWidth(subtitleText);
          }
          if (item.link) {
            doc.setFont("times", "normal");
            doc.text(" | ", currentX, yPos);
            currentX += doc.getTextWidth(" | ");
            const linkUrl = normalizeUrl(item.link);
            doc.setTextColor(0, 0, 238);
            doc.textWithLink("Project Link", currentX, yPos, { url: linkUrl });
            doc.setTextColor(0, 0, 0);
          }
          yPos += lineHeight;
          doc.setFont("times", "normal");
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            checkPageBreak();
            addJustifiedBullet(bullet, marginSide + 6, marginSide + 14, contentWidth - 18);
          });
          yPos += 2;
        }
      });
    });
    // Certifications
    const certSections = (data.customSections || []).filter((s) => /certif|licen/i.test(s.title || ""));
    certSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            checkPageBreak();
            doc.setFontSize(bodySize);
            doc.setFont("times", "normal");
            addJustifiedBullet(item.title, marginSide + 6, marginSide + 14, contentWidth - 18);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        const _ca = (item.title || "").toLowerCase().trim();
        const _cb = (section.title || "").toLowerCase().trim();
        const showTitle = item.title && !(_ca === _cb || _ca.startsWith(_cb) || _cb.startsWith(_ca));
        if (hasContent) {
          checkPageBreak();
          doc.setFontSize(bodySize);
          if (showTitle) {
            doc.setFont("times", "normal");
            doc.text(item.title, marginSide, yPos);
            yPos += lineHeight;
          }
          if (item.subtitle) {
            doc.setFont("times", "italic");
            doc.text(item.subtitle, marginSide, yPos);
            yPos += lineHeight;
          }
          doc.setFont("times", "normal");
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            checkPageBreak();
            addJustifiedBullet(bullet, marginSide + 6, marginSide + 14, contentWidth - 18);
          });
        }
      });
    });
    // Other
    const otherSections = (data.customSections || []).filter((s) => s.title && !/certif|licen|project/i.test(s.title));
    otherSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            checkPageBreak();
            doc.setFontSize(bodySize);
            doc.setFont("times", "normal");
            addJustifiedBullet(item.title, marginSide + 6, marginSide + 14, contentWidth - 18);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        const _oa = (item.title || "").toLowerCase().trim();
        const _ob = (section.title || "").toLowerCase().trim();
        const showTitle = item.title && !(_oa === _ob || _oa.startsWith(_ob) || _ob.startsWith(_oa));
        if (hasContent) {
          checkPageBreak();
          if (showTitle) {
            doc.setFontSize(bodySize);
            doc.setFont("times", "normal");
            doc.text(item.title, marginSide, yPos);
            yPos += lineHeight;
          }
          doc.setFont("times", "normal");
          if (item.description) {
            const descLines = doc.splitTextToSize(item.description, contentWidth);
            descLines.forEach((line: string) => {
              checkPageBreak();
              doc.text(line, marginSide, yPos);
              yPos += lineHeight;
            });
          }
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            checkPageBreak();
            addJustifiedBullet(bullet, marginSide + 6, marginSide + 14, contentWidth - 18);
          });
        }
      });
    });
  };

  const renderSummary = () => {
    if (!(sectionVisibility.summary && data.summary)) return;
    addSectionHeader("Summary");
    doc.setFontSize(bodySize);
    doc.setFont("times", "normal");
    const lines = doc.splitTextToSize(data.summary, contentWidth);
    lines.forEach((line: string) => {
      checkPageBreak();
      doc.text(line, marginSide, yPos);
      yPos += lineHeight;
    });
    yPos += sectionSpacing;
  };

  // Render in user-defined order
  const renderers: Record<string, () => void> = {
    summary: renderSummary,
    education: renderEducation,
    experience: renderExperience,
    skills: renderSkills,
    custom: renderCustom,
  };
  sectionOrder.filter(key => key !== "personal").forEach(key => renderers[key]?.());

  const base = sanitizeFileName(fileNameOverride || `${data.personalInfo.fullName} Resume - Harvard`);
  doc.save(`${base}.pdf`);
};

export const generateHarvardDOCX = async (
  data: ResumeData,
  format: ResumeFormat = "standard",
  fileNameOverride?: string,
  sectionVisibility: SectionVisibility = defaultSectionVisibility,
  sectionOrder: SectionKey[] = DEFAULT_SECTION_ORDER
): Promise<void> => {
  const children: Paragraph[] = [];
  const blackBorder = { bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" } };
  
  const nameSize = format === 'compact' ? 36 : 42; // 18pt or 21pt
  const sectionSizeLarge = 24; // 12pt
  const sectionSizeSmall = 18; // 9pt
  const bodySize = 20; // 10pt
  const sectionSpacing = format === 'compact' ? 60 : 100;

  const bulletIndent = { left: 270, hanging: 90 };
  const addBulletParagraph = (runs: Array<TextRun>, spacingAfter: number = 0) => {
    children.push(
      new Paragraph({
        children: runs,
        indent: bulletIndent,
        spacing: { after: spacingAfter },
        alignment: AlignmentType.JUSTIFIED,
      })
    );
  };

  const rightTabPos = convertInchesToTwip(7.4);

  // Small caps section header
  const addSectionHeader = (title: string) => {
    const firstLetter = title.charAt(0).toUpperCase();
    const rest = title.slice(1).toUpperCase();
    
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: firstLetter,
            bold: true,
            size: sectionSizeLarge,
            font: "Times New Roman",
          }),
          new TextRun({
            text: rest,
            bold: true,
            size: sectionSizeSmall,
            font: "Times New Roman",
          }),
        ],
        border: blackBorder,
        spacing: { before: sectionSpacing, after: 40 },
      })
    );
  };

  // Name centered (bold, not all caps)
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: data.personalInfo.fullName || "Your Name",
          bold: true,
          size: nameSize,
          font: "Times New Roman",
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
    })
  );

  // Contact centered with links
  const contactRuns: (TextRun | ExternalHyperlink)[] = [];
  const addContactPart = (text: string, isLink: boolean = false, url?: string) => {
    if (contactRuns.length > 0) {
      contactRuns.push(new TextRun({ text: " | ", size: bodySize, font: "Times New Roman" }));
    }
    if (isLink && url) {
      contactRuns.push(
        new ExternalHyperlink({
          children: [
            new TextRun({
              text,
              size: bodySize,
              font: "Times New Roman",
              underline: { type: "single" },
            }),
          ],
          link: normalizeUrl(url),
        })
      );
    } else {
      contactRuns.push(new TextRun({ text, size: bodySize, font: "Times New Roman" }));
    }
  };

  if (data.personalInfo.location) addContactPart(data.personalInfo.location);
  if (data.personalInfo.phone) addContactPart(data.personalInfo.phone);
  if (data.personalInfo.email) addContactPart(data.personalInfo.email, true, `mailto:${data.personalInfo.email}`);
  if (data.personalInfo.linkedin) addContactPart("LinkedIn", true, data.personalInfo.linkedin);
  if (data.personalInfo.github) addContactPart("GitHub", true, data.personalInfo.github);
  if (data.personalInfo.portfolio) addContactPart("Portfolio", true, data.personalInfo.portfolio);

  children.push(
    new Paragraph({
      children: contactRuns,
      alignment: AlignmentType.CENTER,
      spacing: { after: sectionSpacing },
    })
  );

  // Section renderers
  const renderDocxEducation = () => {
    if (!(sectionVisibility.education && data.education.length > 0)) return;
    addSectionHeader("Education");
    data.education.forEach((edu) => {
      children.push(new Paragraph({ children: [new TextRun({ text: edu.institution, bold: true, size: bodySize, font: "Times New Roman" })] }));
      let degreeText = edu.degree;
      if (edu.field) degreeText += ` ${edu.field}`;
      if (edu.gpa) degreeText += `, GPA: ${edu.gpa}`;
      children.push(new Paragraph({
        children: [
          new TextRun({ text: degreeText, italics: true, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: edu.location ? `\t${edu.location}` : "", italics: true, size: bodySize, font: "Times New Roman" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
        spacing: { after: 40 },
      }));
    });
  };

  const renderDocxExperience = () => {
    if (!(sectionVisibility.experience && data.experience.length > 0)) return;
    addSectionHeader("Experience");
    data.experience.forEach((exp) => {
      const startFormatted = formatDateFull(exp.startDate);
      const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
      const dateDisplay = startFormatted || endFormatted ? `${startFormatted || ""}${startFormatted && endFormatted ? " – " : ""}${endFormatted || ""}` : "";
      children.push(new Paragraph({
        children: [
          new TextRun({ text: exp.jobTitle, bold: true, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: dateDisplay ? `\t${dateDisplay}` : "", bold: true, size: bodySize, font: "Times New Roman" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
      }));
      const locationDisplay = [exp.location, exp.workType].filter(Boolean).join(" | ");
      children.push(new Paragraph({
        children: [
          new TextRun({ text: exp.company, italics: true, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: locationDisplay ? `\t${locationDisplay}` : "", italics: true, size: bodySize, font: "Times New Roman" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
      }));
      (exp.bullets || []).filter(Boolean).forEach((bullet, idx, arr) => {
        addBulletParagraph([new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })], idx === arr.length - 1 ? 60 : 0);
      });
    });
  };

  const renderDocxSkills = () => {
    if (!(sectionVisibility.skills && data.coreStrengths && data.coreStrengths.length > 0 && data.coreStrengths.some(c => c.category && c.skills))) return;
    addSectionHeader("Technical Skills");
    data.coreStrengths.filter(c => c.category && c.skills).forEach((cat) => {
      children.push(new Paragraph({
        children: [
          new TextRun({ text: `${sanitizeText(cat.category)}: `, bold: true, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: sanitizeText(cat.skills), size: bodySize, font: "Times New Roman" }),
        ],
        spacing: { after: 20 },
      }));
    });
  };

  const renderDocxCustom = () => {
    if (!sectionVisibility.customSections) return;
    const projSections = (data.customSections || []).filter((s) => /project/i.test(s.title || ""));
    projSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            addBulletParagraph([new TextRun({ text: `• ${item.title}`, size: bodySize, font: "Times New Roman" })], 0);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        if (hasContent) {
          const projectRuns: (TextRun | ExternalHyperlink)[] = [];
          if (item.title) projectRuns.push(new TextRun({ text: item.title, bold: true, size: bodySize, font: "Times New Roman" }));
          if (item.subtitle) projectRuns.push(new TextRun({ text: ` | ${item.subtitle}`, italics: true, size: bodySize, font: "Times New Roman" }));
          if (item.link) {
            projectRuns.push(new TextRun({ text: " | ", size: bodySize, font: "Times New Roman" }));
            projectRuns.push(new ExternalHyperlink({ children: [new TextRun({ text: "Project Link", size: bodySize, font: "Times New Roman", underline: { type: "single" } })], link: normalizeUrl(item.link) }));
          }
          if (projectRuns.length > 0) children.push(new Paragraph({ children: projectRuns }));
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            addBulletParagraph([new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })], 0);
          });
        }
      });
    });
    const certSections = (data.customSections || []).filter((s) => /certif|licen/i.test(s.title || ""));
    certSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            addBulletParagraph([new TextRun({ text: `• ${item.title}`, size: bodySize, font: "Times New Roman" })], 0);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        const _dca = (item.title || "").toLowerCase().trim();
        const _dcb = (section.title || "").toLowerCase().trim();
        const showTitle = item.title && !(_dca === _dcb || _dca.startsWith(_dcb) || _dcb.startsWith(_dca));
        if (hasContent) {
          if (showTitle) children.push(new Paragraph({ children: [new TextRun({ text: item.title, size: bodySize, font: "Times New Roman" })] }));
          if (item.subtitle) children.push(new Paragraph({ children: [new TextRun({ text: item.subtitle, italics: true, size: bodySize, font: "Times New Roman" })], spacing: { after: 40 } }));
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            addBulletParagraph([new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })], 0);
          });
        }
      });
    });
    const otherSections = (data.customSections || []).filter((s) => s.title && !/certif|licen|project/i.test(s.title));
    otherSections.forEach((section) => {
      addSectionHeader(section.title);
      (section.items || []).forEach((item) => {
        if (item.type === 'bullet') {
          if (item.title) {
            addBulletParagraph([new TextRun({ text: `• ${item.title}`, size: bodySize, font: "Times New Roman" })], 0);
          }
          return;
        }
        const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
        const _doa = (item.title || "").toLowerCase().trim();
        const _dob = (section.title || "").toLowerCase().trim();
        const showTitle = item.title && !(_doa === _dob || _doa.startsWith(_dob) || _dob.startsWith(_doa));
        if (hasContent) {
          if (showTitle) children.push(new Paragraph({ children: [new TextRun({ text: item.title, size: bodySize, font: "Times New Roman" })] }));
          if (item.description) children.push(new Paragraph({ children: [new TextRun({ text: item.description, size: bodySize, font: "Times New Roman" })] }));
          (item.bullets || []).filter(Boolean).forEach((bullet) => {
            addBulletParagraph([new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })], 0);
          });
        }
      });
    });
  };

  const renderDocxSummary = () => {
    if (!(sectionVisibility.summary && data.summary)) return;
    addSectionHeader("Summary");
    children.push(new Paragraph({
      children: [new TextRun({ text: data.summary, size: bodySize, font: "Times New Roman" })],
      spacing: { after: 40 },
    }));
  };

  // Render in user-defined order
  const renderers: Record<string, () => void> = {
    summary: renderDocxSummary,
    education: renderDocxEducation,
    experience: renderDocxExperience,
    skills: renderDocxSkills,
    custom: renderDocxCustom,
  };
  sectionOrder.filter(key => key !== "personal").forEach(key => renderers[key]?.());

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.4),
              bottom: convertInchesToTwip(0.4),
              left: convertInchesToTwip(0.55),
              right: convertInchesToTwip(0.55),
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const base = sanitizeFileName(fileNameOverride || `${data.personalInfo.fullName} Resume - Harvard`);
  saveAs(blob, `${base}.docx`);
};
